import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';
import { Formik, Form as FormikForm } from 'formik';

import { save } from './services/ScheduleDemo';

import TransitionModal from 'components/TransitionModal';
import Field from './components/Field';
import { Row, Col } from 'components/Grid';
import Alert from 'components/Alert';

import {
  Container,
  Title,
  Subtitle,
  CloseButton,
  Header,
  SubmitButton,
} from './styles';

const Form = ({ show, onClose }) => {
  const { t } = useTranslation();
  const [showAlert, setShowAlert] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const roleItems = [
    {
      value: 'Traveler',
      label: t('FORM.FIELDS.ROLE.ITEMS.TRAVELER')
    },
    {
      value: 'Admin',
      label: t('FORM.FIELDS.ROLE.ITEMS.ADMIN')
    },
    {
      value: 'Finance',
      label: t('FORM.FIELDS.ROLE.ITEMS.FINANCE')
    },
    {
      value: 'HR',
      label: t('FORM.FIELDS.ROLE.ITEMS.HR')
    },
    {
      value: 'C-Level',
      label: t('FORM.FIELDS.ROLE.ITEMS.C_LEVEL')
    },
    {
      value: 'Other',
      label: t('FORM.FIELDS.ROLE.ITEMS.OTHER')
    },
  ];

  const companySizeItems = [
    {
      value: '1-20',
      label: t('FORM.FIELDS.COMPANY_SIZE.ITEMS.UP_TO_20'),
    },
    {
      value: '21-50',
      label: t('FORM.FIELDS.COMPANY_SIZE.ITEMS.UP_TO_50')
    },
    {
      value: '51-200',
      label: t('FORM.FIELDS.COMPANY_SIZE.ITEMS.UP_TO_200')
    },
    {
      value: '201-500',
      label: t('FORM.FIELDS.COMPANY_SIZE.ITEMS.UP_TO_500')
    },
    {
      value: '501-1000',
      label: t('FORM.FIELDS.COMPANY_SIZE.ITEMS.UP_TO_1000')
    },
    {
      value: '1001-5000',
      label: t('FORM.FIELDS.COMPANY_SIZE.ITEMS.UP_TO_5000')
    },
    {
      value: '>5000',
      label: t('FORM.FIELDS.COMPANY_SIZE.ITEMS.MORE_THAN_5000'),
    },
  ];

  const initialValues = {
    name: '',
    email: '',
    phone: '',
    company: '',
    role: '',
    employees: '',
    help: '',
  };

  const validationSchema = yup.object().shape({
    name: yup.string().min(2).max(50).required(),
    email: yup.string().email().required(),
    phone: yup.string().phone().required(),
    company: yup.string().min(3).max(20).required(),
    role: yup.string().required(),
    employees: yup.string().required(),
    help: yup.string().min(0).max(1000),
  });

  const handleSubmit = (data) => {
    setIsLoading(true);
    data.timestamp = new Date();
    data.country = 'Colombia';
    data.unique_refcode = data.name.toLowerCase().replace(/\s/g, '') + Math.floor(Math.random() * 110);

    save(data).then(() => {
      onClose();
      setIsLoading(false);
      setShowAlert(true);
    }).catch((err) => {
      setIsLoading(false);
      console.log(err);
    });
  };

  return (
    <>
      <TransitionModal
        show={show}
        onClose={onClose}
      >
        <Container>
          <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
          >
            <FormikForm>
              <Header>
                <Title>{t('FORM.TITLE')}</Title>
                <CloseButton
                  onClick={onClose}
                  src="/assets/icons/form/close.svg"
                  alt="close-form"
                />
              </Header>
              <Subtitle>{t('FORM.SUBTITLE')}</Subtitle>
              <Row align="start" justify="between" gutterWidth={30}>
                <Col xs={12} sm={6}>
                  <Field
                    label={t('FORM.FIELDS.NAME')}
                    name="name"
                    clearIcon
                  />
                </Col>
                <Col xs={12} sm={6}>
                  <Field
                    label={t('FORM.FIELDS.EMAIL')}
                    name="email"
                    clearIcon
                  />
                </Col>
              </Row>

              <Row align="start" justify="between" gutterWidth={30}>
                <Col xs={12} sm={6}>
                  <Field
                    label={t('FORM.FIELDS.PHONE_NUMBER')}
                    name="phone"
                    clearIcon
                  />
                </Col>
                <Col xs={12} sm={6}>
                  <Field
                    label={t('FORM.FIELDS.COMPANY_NAME')}
                    name="company"
                    clearIcon
                  />
                </Col>
              </Row>

              <Row align="start" justify="between" gutterWidth={30}>
                <Col xs={12} sm={6}>
                  <Field
                    label={t('FORM.FIELDS.ROLE.NAME')}
                    name="role"
                    selectItems={roleItems}
                  />
                </Col>
                <Col xs={12} sm={6}>
                  <Field
                    label={t('FORM.FIELDS.COMPANY_SIZE.NAME')}
                    name="employees"
                    selectItems={companySizeItems}
                  />
                </Col>
              </Row>
              
              <Field
                label={t('FORM.FIELDS.HELP')}
                name="help"
                isMultiline
              />

              <Row justify="center">
                <SubmitButton isLoading={isLoading} type="submit">{t('FORM.CONFIRM_BUTTON')}</SubmitButton>
              </Row>
            </FormikForm>
          </Formik>
        </Container>
      </TransitionModal>
      <Alert
        show={showAlert}
        onClose={() => setShowAlert(false)}
        title={t('FORM.ALERT.TITLE')}
        subtitle={t('FORM.ALERT.SUBTITLE')}
        buttonText={t('FORM.ALERT.BUTTON')}
      />
    </>
  );
};

Form.propTypes = {
  show: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default Form;